import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { PlusIcon } from "@heroicons/react/24/outline";
import Button from "../../Button";
import Table from "../../Table";
import { useEffect, useMemo, useState } from "react";
import { Toast } from "../../Toast";
import { DocusignConfig } from "../../../models/DocusignConfig";
import { PencilIcon, StarIcon, TrashIcon } from "@heroicons/react/24/solid";
import { deleteAgencyDocusignConfig, setAgencyMainDocusignConfig } from "../../../state/docusignConfig/actions";
import { Agency } from "../../../models/Agency";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../LoadingMask";
import { AgencyDocusignConfigModal } from "./AgencyDocusignConfigModal";
import { SetAgencyMainDocusignConfigModal } from "./SetAgencyMainDocusignConfigModal";
import { AgencyDeleteDocusignConfigModal } from "./AgencyDeleteDocusignConfigModal";

interface Props {
    currentAgency: Agency
}

export const AgencyDocusignConfigModule = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [showDConfigError, setShowDConfigError] = useState(false);
    const isLoadingConfig = useSelector((state: RootState) => state.docusignConfig.isLoading);
    const docusignConfigs = useSelector((state: RootState) => state.docusignConfig.docusignConfigs);
    const docusignConfigError = useSelector((state: any) => state.docusignConfig.error);

    const [showDocusignConfigModal, setShowDocusignConfigModal] = useState<{
        show: boolean;
        update: boolean;
        docusignConfig?: DocusignConfig;
        index?: number;
    }>({ show: false, update: false });

    const [ showSetMainDocusignConfigModal, setShowSetMainDocusignConfigModal] = useState<{
        docusignConfig?: DocusignConfig;
        show: boolean;
    }>({ show: false, docusignConfig: undefined });

    const [ showDeleteDocusignConfigModal, setShowDeleteDocusignConfigModal] = useState<{
        docusignConfig?: DocusignConfig;
        show: boolean;
    }>({ show: false, docusignConfig: undefined });

    useEffect(() => {
        if (showDConfigError) {
            Toast(t(docusignConfigError?.reason ?? "SOMETHING_WENT_WRONG"), t(docusignConfigError?.cause_info));
            setShowDConfigError(false);
        }
    }, [showDConfigError, docusignConfigError, t]);

    const getDocusignConfigsRows = useMemo(() => {
        return docusignConfigs?.map((config: DocusignConfig) => {
            return {
                columns: [
                    { value: config.label },
                    { value: config.docusign_integration_key },
                    { value: config.docusign_account_id },
                    { value: config.docusign_user_id },
                    {
                        value: (
                        <div className="flex flex-row justify-center divide-x">
                            <Button
                                isTerciary
                                iconOnly
                                tooltip={t("UPDATE_CONFIG")}
                                onClick={() => setShowDocusignConfigModal(
                                    {
                                    show: true,
                                    update: true,
                                    docusignConfig: config
                                    }
                                )}
                                classNames="mr-2"
                            >
                                <PencilIcon className="h-5 aspect-square text-blue" />
                            </Button>
                            <Button
                                isTerciary
                                iconOnly
                                isDisabled={config.is_main_config === "1"}
                                tooltip={t("DELETE_CONFIG")}
                                onClick={() => setShowDeleteDocusignConfigModal(
                                    {
                                    show: true,
                                    docusignConfig: config
                                    }
                                )}
                                classNames="mx-2"
                            >
                                <TrashIcon className="h-5 aspect-square text-red-600" />
                            </Button>
                            <Button
                                isTerciary
                                iconOnly
                                isDisabled={config.is_main_config === "1"}
                                tooltip={t("SET_AS_MAIN_CONFIG")}
                                onClick={() => setShowSetMainDocusignConfigModal(
                                    {
                                    show: true,
                                    docusignConfig: config
                                    }
                                )}
                                classNames="mr-2"
                            >
                                <StarIcon className="h-5 aspect-square text-gold" />
                            </Button>
                        </div>
                        ),
                    },
                ],
            };
        });
    }, [docusignConfigs, t]);

    const onSetMainDocusignConfig = (config: DocusignConfig) => {
        dispatch(setAgencyMainDocusignConfig({agencyId: props.currentAgency.id, configId: config.id!})).then((e) => {
            if (e.type === "agencies/setAgencyMainDocusignConfig/rejected") {
                setShowDConfigError(true);
            } else {
                toast(t("SUCCESSFULLY_SET_DOCUSIGN_CONFIG"));
            }
        });
    };

    const onDeleteDocusignConfig = (config: DocusignConfig) => {
        dispatch(deleteAgencyDocusignConfig({agencyId: props.currentAgency.id, configId: config.id!})).then((e) => {
            if (e.type === "agencies/deleteAgencyDocusignConfig/rejected") {
                setShowDConfigError(true);
            } else {
                toast(t("SUCCESSFULLY_DELETE_DOCUSIGN_CONFIG"));
            }
        });
    };
    
    return(
        <>
            {isLoadingConfig && <LoadingMask />}
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            <AgencyDocusignConfigModal
                info={showDocusignConfigModal}
                onClose={() => setShowDocusignConfigModal({ show: false, update: false })}
            />
            <SetAgencyMainDocusignConfigModal
                info={showSetMainDocusignConfigModal}
                onClose={() => setShowSetMainDocusignConfigModal({show : false, docusignConfig: undefined})}
                setMainConfig={(config: DocusignConfig) => onSetMainDocusignConfig(config)}
            />
            <AgencyDeleteDocusignConfigModal
                info={showDeleteDocusignConfigModal}
                onClose={() => setShowDeleteDocusignConfigModal({show : false, docusignConfig: undefined})}
                deleteConfig={(config: DocusignConfig) => onDeleteDocusignConfig(config)}
            />
            <div className="text-black max-h-full p-5 md:w-full">
                <div className="flex justify-between w-full">
                    <div className="text-md font-bold text-black pl-3 mt-2">
                        {t("DOCUSIGN_CONFIG")}
                    </div>
                    <Button onClick={() => setShowDocusignConfigModal({ show: true, update: false })}>
                        <span className="flex items-center font-semibold pr-3">
                            <div className="w-8 p-1 aspect-square mr-2">
                                <PlusIcon />
                            </div>
                            {t("ADD_DOCUSIGN_CONFIG")}
                        </span>
                    </Button>
                </div>
                <div className="w-full my-4">
                    <Table
                        columns={[
                            { header: `${t("INTEGRATION_NAME")}` },
                            { header: `${t("INTEGRATION_KEY")}` },
                            { header: `${t("ACCOUNT_ID")}` },
                            { header: `${t("USER_ID")}` },
                            { header: "" },
                        ]}
                        data={getDocusignConfigsRows}
                    />
                </div>
            </div>
        </>
    )
}